import { useEffect, useState } from 'react'
import ShareInvite from '../components/ShareInvite'
import { api } from '../lib/api'
import type { User, AuditRow } from '../lib/types'

type AdminUser = User & { tree_count: number; created_at: string; last_login?: string; invite_code_used?: string }
type Invite = { code: string; label: string; max_uses: number; used_count: number; active: boolean; expires_at?: string }
type Tab = 'insights' | 'onboarding' | 'users' | 'ai' | 'alerts' | 'support' | 'promo' | 'codes' | 'audit'

const TABS: [Tab, string][] = [
  ['insights', '📊 Insights'], ['onboarding', '🌱 Onboarding'], ['users', '👥 Users'], ['ai', '🧙 AI Monitor'], ['alerts', '🔔 Alerts'],
  ['support', '🛟 Support'], ['promo', '🎟 Promo'], ['codes', '🔑 Invites'], ['audit', '📜 Audit'],
]

export default function Admin() {
  const [tab, setTab] = useState<Tab>('insights')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [codes, setCodes] = useState<Invite[]>([])
  const [newLabel, setNewLabel] = useState('')
  const [inviteTree, setInviteTree] = useState('')
  const [allTrees, setAllTrees] = useState<{ id: string; name: string }[]>([])
  const [audit, setAudit] = useState<AuditRow[]>([])
  const [auditActor, setAuditActor] = useState('')
  const [insights, setInsights] = useState<Awaited<ReturnType<typeof api.adminInsights>> | null>(null)
  const [onboard, setOnboard] = useState<Awaited<ReturnType<typeof api.adminOnboarding>> | null>(null)
  const [aiLog, setAiLog] = useState<Awaited<ReturnType<typeof api.adminAiMonitor>>>([])
  const [aiFlagged, setAiFlagged] = useState(false)
  const [alerts, setAlerts] = useState<Awaited<ReturnType<typeof api.adminAlerts>>>([])
  const [tickets, setTickets] = useState<Awaited<ReturnType<typeof api.adminListTickets>>>([])
  const [promo, setPromo] = useState<Awaited<ReturnType<typeof api.adminListPromo>>>([])
  const [pf, setPf] = useState({ kind: 'discount', label: '', percent_off: 20, lock_years: 1, applies_to: 'any', max_uses: 0 })

  const loadAudit = (actor?: string) => api.adminAudit(actor ? { actor } : undefined).then(setAudit).catch(() => {})
  const loadUsers = () => api.adminListUsers().then(setUsers).catch(() => {})
  const [grantMsg, setGrantMsg] = useState('')

  /**
   * Give someone headroom (D-144). Until this existed, every limit in the app
   * refused people with advice nobody could act on — the checkout cannot charge
   * a card, and "ask the keeper for more room" pointed at a keeper with no button.
   */
  const grant = async (userId: string, email: string, kind: 'plan' | 'storage_mb' | 'credits') => {
    const ask = kind === 'plan'
      ? prompt(`Move ${email} to which plan?  seedling · heritage · dynasty`, 'heritage')
      : prompt(kind === 'storage_mb' ? `Extra storage for ${email}, in MB (negative takes it back):` : `Credits for ${email} (negative takes them back):`,
               kind === 'storage_mb' ? '500' : '100')
    if (!ask) return
    try {
      const r = await api.grant(userId, kind, kind === 'plan' ? ask : Number(ask))
      setGrantMsg(`✓ ${r.email}: ${r.granted}`)
      loadUsers()
    } catch (e) {
      setGrantMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '').trim() : 'Grant failed')
    }
  }
  const loadCodes = () => api.adminListInviteCodes().then(setCodes).catch(() => {})
  const loadAlerts = () => api.adminAlerts().then(setAlerts).catch(() => {})
  const loadTickets = () => api.adminListTickets().then(setTickets).catch(() => {})

  // Advance a ticket; optionally email the reporter (keeper-gated, per action — D-069)
  const resolveTicket = async (id: string, status: string, askNote = false) => {
    const note = askNote ? (prompt('Note to include (optional):') || undefined) : undefined
    const notify = confirm(`Email the reporter that this is now "${status}"?` + (note ? `\n\nYour note will be included.` : ''))
    const r = await api.adminUpdateTicket(id, status, note, notify)
    if (notify) alert(r.notified ? 'Reporter emailed.' : 'Status updated (no email sent for this transition).')
    loadTickets()
  }
  const loadPromo = () => api.adminListPromo().then(setPromo).catch(() => {})

  useEffect(() => {
    api.adminInsights().then(setInsights).catch(() => {})
    loadUsers(); loadAlerts()
  }, [])
  useEffect(() => {
    if (tab === 'audit') loadAudit()
    if (tab === 'ai') api.adminAiMonitor(aiFlagged).then(setAiLog).catch(() => {})
    if (tab === 'support') loadTickets()
    if (tab === 'onboarding') api.adminOnboarding().then(setOnboard).catch(() => {})
    if (tab === 'promo') loadPromo()
    if (tab === 'codes') { loadCodes(); api.listTrees().then(ts => setAllTrees(ts.map(t => ({ id: t.id, name: t.name })))).catch(() => {}) }
  }, [tab, aiFlagged])

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold text-slate-100">Keeper's Console</h1>
        {(insights?.open_alerts || 0) > 0 && (
          <button onClick={() => setTab('alerts')} className="text-xs px-3 py-1.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">
            🔔 {insights!.open_alerts} open alert{insights!.open_alerts > 1 ? 's' : ''}
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 ${tab === k ? 'bg-white/10 text-gold-300' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Insights ── */}
      {tab === 'insights' && insights && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[['Users', insights.users, `${insights.approved_users} approved`], ['Constellations', insights.constellations, `${insights.souls} souls`],
              ['Circles', insights.circles, ''], ['AI msgs (24h)', insights.ai_messages_24h, `${insights.mutations_24h} actions`],
              ['Off-topic total', insights.off_topic_total, 'flagged'], ['Open alerts', insights.open_alerts, ''],
              ['Storage', `${((insights.storage_total_bytes || 0) / 1024 / 1024 / 1024).toFixed(2)} GB`, `${insights.media_files || 0} files · ${insights.media_photos || 0} 📸 ${insights.media_videos || 0} 🎞`],
              ['Pending claims', insights.pending_claims || 0, 'identity requests']].map(([label, val, sub], i) => (
              <div key={i} className="card-glass p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                <p className="font-display text-2xl font-bold text-gradient-gold">{val}</p>
                {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="card-glass p-4">
              <h3 className="font-display font-semibold text-slate-100 mb-2">🚫 What the app refused (30d)</h3>
              <p className="text-[10px] text-slate-600 mb-2">Limits that blocked someone. A refusal nobody sees looks like a broken app.</p>
              {((insights as unknown as { refusals?: { status: number; path: string; n: number; last_seen: string }[] }).refusals || []).map((r, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span className="text-slate-300 truncate">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded mr-1.5 ${r.status === 402 ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-500/20 text-slate-400'}`}>{r.status}</span>
                    {r.path}
                  </span>
                  <span className="text-slate-500">×{r.n}</span>
                </div>
              ))}
              {!((insights as unknown as { refusals?: unknown[] }).refusals || []).length && <p className="text-sm text-slate-500">Nothing was refused. Good.</p>}
            </div>
            <div className="card-glass p-4">
              <h3 className="font-display font-semibold text-slate-100 mb-2">Top Sage users</h3>
              {insights.top_askers.map((a, i) => (
                <div key={i} className="flex justify-between text-sm py-1"><span className="text-slate-300 truncate">{a.who}</span><span className="text-slate-500">{a.msgs} msgs</span></div>
              ))}
              {insights.top_askers.length === 0 && <p className="text-sm text-slate-500">No AI usage yet.</p>}
            </div>
            <div className="card-glass p-4">
              <h3 className="font-display font-semibold text-slate-100 mb-2">Plan distribution</h3>
              {insights.plans.map((p, i) => (
                <div key={i} className="flex justify-between text-sm py-1"><span className="text-slate-300 capitalize">{p.plan || 'seedling'}</span><span className="text-slate-500">{p.c}</span></div>
              ))}
            </div>
            <div className="card-glass p-4">
              <h3 className="font-display font-semibold text-slate-100 mb-2">Top storage users</h3>
              {(insights.top_storage || []).map((s: { who: string; bytes: number; files: number }, i: number) => (
                <div key={i} className="flex justify-between text-sm py-1"><span className="text-slate-300 truncate">{s.who}</span><span className="text-slate-500">{(s.bytes / 1024 / 1024).toFixed(0)} MB · {s.files} files</span></div>
              ))}
              {(insights.top_storage || []).length === 0 && <p className="text-sm text-slate-500">No uploads yet.</p>}
            </div>
            <div className="card-glass p-4">
              <h3 className="font-display font-semibold text-slate-100 mb-2">Upload scan status</h3>
              {(insights.scan_breakdown || []).map((s: { s: string; c: number }, i: number) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span className={`capitalize ${s.s === 'infected' ? 'text-red-400' : s.s === 'flagged' ? 'text-amber-300' : 'text-slate-300'}`}>{s.s}</span>
                  <span className="text-slate-500">{s.c}</span>
                </div>
              ))}
              {(insights.scan_breakdown || []).length === 0 && <p className="text-sm text-slate-500">No uploads yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding journey ── */}
      {tab === 'onboarding' && onboard && (
        <div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-5">
            {([['Registered', onboard.funnel.registered], ['Approved', onboard.funnel.approved], ['Named', onboard.funnel.named],
               ['Placed', onboard.funnel.placed], ['Contributed', onboard.funnel.contributed], ['Met Sage', onboard.funnel.met_sage],
               ['Toured', onboard.funnel.toured], ['⚠ Stalled', onboard.funnel.stalled]] as const).map(([label, n]) => (
              <div key={label} className={`card-glass p-3 text-center ${label === '⚠ Stalled' && n > 0 ? '!border-amber-500/40' : ''}`}>
                <p className={`font-display text-xl font-bold ${label === '⚠ Stalled' && n > 0 ? 'text-amber-300' : 'text-gradient-gold'}`}>{n}</p>
                <p className="text-[9px] uppercase tracking-wide text-slate-500">{label}</p>
              </div>
            ))}
          </div>
          <div className="card-glass p-5 overflow-x-auto">
            <table className="w-full text-xs min-w-[880px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 border-b border-white/10">
                <th className="pb-2 pr-3">Member</th><th className="pb-2 pr-3">Stage</th><th className="pb-2 pr-3">Registered</th>
                <th className="pb-2 pr-3">Last seen</th><th className="pb-2 pr-3">Trees</th><th className="pb-2 pr-3">Souls added</th>
                <th className="pb-2 pr-3">Sage</th><th className="pb-2 pr-3">Media</th><th className="pb-2 pr-3">API 24h</th><th className="pb-2">Flags</th></tr></thead>
              <tbody>
                {onboard.members.map(m => (
                  <tr key={String(m.id)} className={`border-b border-white/[0.04] ${m.stalled ? 'bg-amber-500/[0.05]' : ''}`}>
                    <td className="py-2 pr-3 text-slate-200">{String(m.name || m.email)}<span className="block text-[10px] text-slate-600">{m.name ? String(m.email) : 'no display name'}</span></td>
                    <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[10px] ${
                      m.stage === 'engaged' ? 'bg-emerald-500/15 text-emerald-300' :
                      m.stage === 'building' ? 'bg-teal-500/15 text-teal-300' :
                      m.stage === 'pending-approval' ? 'bg-red-500/15 text-red-300' :
                      'bg-amber-500/15 text-amber-300'}`}>{String(m.stage)}</span></td>
                    <td className="py-2 pr-3 text-slate-500">{String(m.created_at).slice(0, 10)}</td>
                    <td className="py-2 pr-3 text-slate-400">{String(m.last_action || m.last_login || '—').slice(0, 10)}</td>
                    <td className="py-2 pr-3 text-slate-400">{Number(m.trees_owned)}{Number(m.shared_trees) > 0 ? ` +${m.shared_trees}sh` : ''}{Number(m.circles) > 0 ? ` ·${m.circles}c` : ''}</td>
                    <td className="py-2 pr-3 text-slate-400">{Number(m.persons_added)}</td>
                    <td className="py-2 pr-3 text-slate-400">{Number(m.sage_msgs)}</td>
                    <td className="py-2 pr-3 text-slate-400">{Number(m.media_uploaded)}</td>
                    <td className="py-2 pr-3 text-slate-500">{Number(m.api_calls_24h)}</td>
                    <td className="py-2 text-[10px]">
                      {m.stalled ? <span className="text-amber-300">⚠ stalled </span> : null}
                      {Number(m.pending_claims) > 0 ? <span className="text-rootv-300">✋{Number(m.pending_claims)} </span> : null}
                      {Number(m.claimed_persons) > 0 ? <span className="text-emerald-400">linked </span> : null}
                      {m.tour_done ? <span className="text-slate-600">toured</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-600 mt-3">Stage: pending-approval → no-tree → tree-but-empty → building → engaged. Stalled = approved &gt;24h ago with zero activity.</p>
          </div>
        </div>
      )}

      {/* ── Users ── */}
      {tab === 'users' && (
        <div className="card-glass p-5 overflow-x-auto">
          <p className="text-xs text-slate-500 mb-1">
            <b className="text-slate-300">Give room, not excuses.</b> Every limit here refuses people with advice they cannot act on —
            the checkout is in test mode, so “manage your plan on the website” is a dead end. These buttons are the remedy the app now points at.
          </p>
          {grantMsg && <p className="text-xs text-gold-300 mb-3">{grantMsg}</p>}
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-600 border-b border-white/10">
              <th className="pb-2 pr-3">Email</th><th className="pb-2 pr-3">Role</th><th className="pb-2 pr-3">Trees</th>
              <th className="pb-2 pr-3">Storage</th><th className="pb-2 pr-3">Media</th>
              <th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">Last login</th><th className="pb-2"></th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-white/[0.04]">
                  <td className="py-2.5 pr-3 text-slate-200">{u.email}</td>
                  <td className="py-2.5 pr-3 text-slate-400">{u.role}</td>
                  <td className="py-2.5 pr-3 text-slate-400">{u.tree_count}</td>
                  <td className="py-2.5 pr-3 text-slate-400 text-xs">{(((u as AdminUser & { storage_bytes?: number }).storage_bytes || 0) / 1024 / 1024).toFixed(0)} MB</td>
                  <td className="py-2.5 pr-3 text-slate-500 text-xs">{(u as AdminUser & { photo_count?: number }).photo_count || 0}📸 {(u as AdminUser & { video_count?: number }).video_count || 0}🎞 {(u as AdminUser & { doc_count?: number }).doc_count || 0}📜</td>
                  <td className="py-2.5 pr-3"><span className={`px-2 py-0.5 rounded-full text-[11px] ${u.approved ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{u.approved ? 'approved' : 'pending'}</span></td>
                  <td className="py-2.5 pr-3 text-slate-500 text-xs">{u.last_login?.slice(0, 16) || '—'}</td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    {u.role !== 'admin' && <>
                      <button onClick={async () => { await api.adminApproveUser(u.id, !u.approved); loadUsers() }} className="text-xs text-gold-400 hover:text-gold-300 mr-3">{u.approved ? 'Revoke' : 'Approve'}</button>
                      <button onClick={() => grant(u.id, u.email, 'plan')} className="text-xs text-teal-400/80 hover:text-teal-300 mr-3" title="Move this person to another plan">Plan</button>
                      <button onClick={() => grant(u.id, u.email, 'storage_mb')} className="text-xs text-teal-400/80 hover:text-teal-300 mr-3" title="Give extra storage">+MB</button>
                      <button onClick={() => grant(u.id, u.email, 'credits')} className="text-xs text-teal-400/80 hover:text-teal-300 mr-3" title="Give Sage credits">+⚡</button>
                      <button onClick={async () => { if (confirm(`Delete ${u.email} and ALL their data?`)) { await api.adminDeleteUser(u.id); loadUsers() } }} className="text-xs text-red-400/70 hover:text-red-400">Delete</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── AI Monitor ── */}
      {tab === 'ai' && (
        <div className="card-glass p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-slate-100">Sage Conversations</h2>
            <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={aiFlagged} onChange={e => setAiFlagged(e.target.checked)} className="accent-gold-500" />Off-topic only</label>
          </div>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {aiLog.map(l => (
              <div key={l.id} className={`px-3 py-2 rounded-lg text-xs ${l.off_topic ? 'bg-red-500/10 border border-red-500/20' : l.role === 'user' ? 'bg-white/[0.04]' : 'bg-white/[0.02]'}`}>
                <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                  <span>{l.who} · {l.role}{l.off_topic ? ' · ⚠ off-topic' : ''}</span><span>{l.created_at.slice(5, 16).replace('T', ' ')}</span>
                </div>
                <p className="text-slate-300">{l.content}</p>
              </div>
            ))}
            {aiLog.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No conversations logged.</p>}
          </div>
          <p className="text-[10px] text-slate-600 mt-3">AI monitoring is disclosed in the privacy policy. Content is retained for abuse review.</p>
        </div>
      )}

      {/* ── Alerts ── */}
      {tab === 'alerts' && (
        <div className="card-glass p-5">
          <h2 className="font-display text-lg font-semibold text-slate-100 mb-4">Behavior Alerts</h2>
          <div className="space-y-2">
            {alerts.map(a => (
              <div key={a.id} className={`flex items-start justify-between gap-3 px-3 py-2 rounded-xl border ${a.acknowledged ? 'bg-white/[0.02] border-white/[0.05] opacity-60' : a.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/25'}`}>
                <div className="min-w-0">
                  <p className="text-sm text-slate-200">{a.kind} <span className="text-[10px] text-slate-500">({a.severity})</span></p>
                  <p className="text-xs text-slate-400">{a.detail}</p>
                  <p className="text-[10px] text-slate-600">{a.who || 'unknown'} · {a.created_at.slice(5, 16).replace('T', ' ')}</p>
                </div>
                {!a.acknowledged && <button onClick={async () => { await api.adminAckAlert(a.id); loadAlerts() }} className="text-xs text-gold-400 hover:text-gold-300 flex-shrink-0">Ack</button>}
              </div>
            ))}
            {alerts.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No alerts — all quiet.</p>}
          </div>
        </div>
      )}

      {/* ── Support ── */}
      {tab === 'support' && (
        <div className="card-glass p-5">
          <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Support Queue</h2>
          <p className="text-xs text-slate-500 mb-4">Nothing enters the work queue without your approval. Flow: new → reviewing → approved → queued.</p>
          <div className="space-y-2">
            {tickets.map(t => (
              <div key={t.id} className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100">{t.kind === 'bug' ? '🐛' : t.kind === 'feature' ? '✨' : '💬'} {t.subject}</p>
                    {t.body && <p className="text-xs text-slate-500 mt-1">{t.body}</p>}
                    <p className="text-[10px] text-slate-600 mt-1">{t.reporter} · {t.created_at.slice(5, 16).replace('T', ' ')}</p>
                    {t.admin_note && <p className="text-[11px] text-gold-300 mt-1">Note: {t.admin_note}</p>}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-slate-300 flex-shrink-0">{t.status}</span>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {t.status === 'new' && <>
                    <button onClick={() => resolveTicket(t.id, 'reviewing')} className="text-[11px] px-2 py-1 rounded-lg bg-rootv-500/15 text-rootv-400">Start review</button>
                    <button onClick={() => resolveTicket(t.id, 'declined', true)} className="text-[11px] px-2 py-1 rounded-lg text-red-400/80">Decline</button>
                  </>}
                  {t.status === 'reviewing' && <>
                    <button onClick={() => resolveTicket(t.id, 'approved', true)} className="text-[11px] px-2 py-1 rounded-lg bg-gold-500/15 text-gold-300">✓ Approve</button>
                    <button onClick={() => resolveTicket(t.id, 'declined', true)} className="text-[11px] px-2 py-1 rounded-lg text-red-400/80">Decline</button>
                  </>}
                  {t.status === 'approved' && <button onClick={() => resolveTicket(t.id, 'queued')} className="text-[11px] px-2 py-1 rounded-lg bg-teal-500/15 text-teal-300">→ Add to work queue</button>}
                  {['queued', 'declined', 'approved'].includes(t.status) && <button onClick={() => resolveTicket(t.id, 'closed', true)} className="text-[11px] px-2 py-1 rounded-lg text-slate-500">Close</button>}
                </div>
              </div>
            ))}
            {tickets.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No support tickets.</p>}
          </div>
        </div>
      )}

      {/* ── Promo codes ── */}
      {tab === 'promo' && (
        <div className="card-glass p-5">
          <h2 className="font-display text-lg font-semibold text-slate-100 mb-4">Promo & Billing Codes</h2>
          <div className="grid sm:grid-cols-3 gap-2 mb-3">
            <select value={pf.kind} onChange={e => setPf({ ...pf, kind: e.target.value })} className="input-dark !py-2 text-sm">
              <option value="discount">Discount (one-off %)</option>
              <option value="credit">Credit (monthly %)</option>
              <option value="lockin">Lock-in (years)</option>
            </select>
            {pf.kind === 'lockin' ? (
              <select value={pf.lock_years} onChange={e => setPf({ ...pf, lock_years: +e.target.value })} className="input-dark !py-2 text-sm">
                {[1, 2, 3].map(y => <option key={y} value={y}>{y} year{y > 1 ? 's' : ''}</option>)}
              </select>
            ) : (
              <input type="number" min={1} max={100} value={pf.percent_off} onChange={e => setPf({ ...pf, percent_off: +e.target.value })} placeholder="% off" className="input-dark !py-2 text-sm" />
            )}
            <input type="number" min={0} value={pf.max_uses} onChange={e => setPf({ ...pf, max_uses: +e.target.value })} placeholder="Max uses (0=∞)" className="input-dark !py-2 text-sm" />
          </div>
          <div className="flex gap-2 mb-4">
            <input value={pf.label} onChange={e => setPf({ ...pf, label: e.target.value })} placeholder="Label (e.g. Summer promo)" className="input-dark !py-2 text-sm flex-1" />
            <button onClick={async () => { await api.adminCreatePromo(pf); loadPromo() }} className="btn-gold !py-2 text-sm">Create code</button>
          </div>
          <div className="space-y-2">
            {promo.map(c => (
              <div key={c.code} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <div><span className="font-mono text-sm text-gold-300">{c.code}</span>
                  <span className="text-[11px] text-slate-500 ml-2">{c.kind === 'lockin' ? `${c.lock_years}yr lock` : `${c.percent_off}% ${c.kind}`}{c.label ? ` · ${c.label}` : ''}</span></div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-500">{c.used_count}{c.max_uses ? `/${c.max_uses}` : ''} used</span>
                  {c.active ? <button onClick={async () => { await api.adminDeactivatePromo(c.code); loadPromo() }} className="text-red-400/70 hover:text-red-400">Off</button> : <span className="text-slate-600">inactive</span>}
                </div>
              </div>
            ))}
            {promo.length === 0 && <p className="text-sm text-slate-500">No promo codes yet.</p>}
          </div>
        </div>
      )}

      {/* ── Invite codes ── */}
      {tab === 'codes' && (
        <div className="card-glass p-5">
          <h2 className="font-display text-lg font-semibold text-slate-100 mb-4">Account Invite Codes</h2>
          <div className="flex gap-2 mb-4">
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (e.g. cousins batch)" className="input-dark flex-1" />
            <select value={inviteTree} onChange={e => setInviteTree(e.target.value)} className="input-dark !py-2 max-w-[220px]" title="Seat the invitee inside this constellation">
              <option value="">↳ no tree (they start empty)</option>
              {allTrees.map(t => <option key={t.id} value={t.id}>↳ joins {t.name}</option>)}
            </select>
            <button onClick={async () => { await api.adminCreateInviteCode({
                label: newLabel || undefined, maxUses: 10, treeId: inviteTree || undefined,
                // a link that can be forwarded should go stale on its own (14 days) —
                // an unlimited-lifetime credential is the worst default available
                expiresAt: new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 19).replace('T', ' '),
              }); setNewLabel(''); loadCodes() }} className="btn-gold !py-2">Generate</button>
          </div>
          <div className="space-y-2">
            {codes.map(c => (
              <div key={c.code} className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <div className="flex items-center justify-between">
                  <div><span className="font-mono text-sm text-gold-300">{c.code}</span>{c.label && <span className="text-xs text-slate-500 ml-2">{c.label}</span>}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-500">{c.used_count}{c.max_uses ? `/${c.max_uses}` : ''} used</span>
                    {c.active ? <button onClick={async () => { await api.adminDeactivateCode(c.code); loadCodes() }} className="text-red-400/70 hover:text-red-400">Deactivate</button> : <span className="text-slate-600">inactive</span>}
                  </div>
                </div>
                {c.active && <ShareInvite code={c.code} treeName={allTrees.find(t => t.id === (c as Invite & { tree_id?: string }).tree_id)?.name} />}
              </div>
            ))}
            {codes.length === 0 && <p className="text-sm text-slate-500">No invite codes yet.</p>}
          </div>
          <p className="text-[10px] text-slate-600 mt-3">Account invites default to 10 uses to curb sharing. Circle join-links are managed per-circle with their own limits.</p>
        </div>
      )}

      {/* ── Audit ── */}
      {tab === 'audit' && (
        <div className="card-glass p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-lg font-semibold text-slate-100">Audit Trail</h2>
            <div className="flex gap-2 items-center">
              <button onClick={() => api.downloadAuditExport().catch(() => {})} className="btn-ghost !py-1.5 !px-3 text-xs" title="Download the full trail as NDJSON for a SIEM">⬇ NDJSON</button>
              <form onSubmit={e => { e.preventDefault(); loadAudit(auditActor.trim() || undefined) }} className="flex gap-2">
                <input value={auditActor} onChange={e => setAuditActor(e.target.value)} placeholder="Filter by actor email…" className="input-dark !py-1.5 text-xs w-52" />
                <button type="submit" className="btn-ghost !py-1.5 !px-3 text-xs">Filter</button>
              </form>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[700px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 border-b border-white/10">
                <th className="pb-2 pr-3">When (UTC)</th><th className="pb-2 pr-3">Actor</th><th className="pb-2 pr-3">Action</th>
                <th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">IP</th><th className="pb-2">Geo</th></tr></thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.id} className="border-b border-white/[0.04]">
                    <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{a.created_at.slice(0, 19)}</td>
                    <td className="py-1.5 pr-3 text-slate-300 truncate max-w-[160px]">{a.actor_email || 'anonymous'}</td>
                    <td className="py-1.5 pr-3 font-mono text-slate-300">{a.method} {a.path}</td>
                    <td className={`py-1.5 pr-3 ${a.status < 400 ? 'text-emerald-400' : a.status === 451 ? 'text-amber-400' : 'text-red-400'}`}>{a.status}</td>
                    <td className="py-1.5 pr-3 text-slate-500 font-mono">{a.ip || '—'}</td>
                    <td className="py-1.5 text-slate-500">{a.country || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {audit.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No audit rows yet.</p>}
          </div>
          <p className="text-[10px] text-slate-600 mt-3">Append-only · every API mutation + blocked-region attempts · 400-day retention · erased users pseudonymized.</p>
        </div>
      )}
    </div>
  )
}
